import React from 'react'
import { useLocalStorage } from 'react-use'

import BUILD_VERSION from 'getBuildVersion'
import { isPlainObject } from 'utils/obj'

export interface AppRefresh {
  hasNewVersion: boolean
  doRefreshCheck: () => void
  markAsRefreshed: () => void
}

export const AppRefreshContext = React.createContext<AppRefresh>({
  hasNewVersion: false,
  doRefreshCheck: () => {},
  markAsRefreshed: () => {}
})

// check every 15 min
const POLLING_INTERVAL = 15 * 60_000

export const AppRefreshProvider: React.FC = ({ children }) => {
  const [hasNewVersion, setHasNewVersion] = useLocalStorage(
    '_appHasNewVersion',
    false
  )

  const refreshVersion = React.useCallback(async () => {
    const cacheBuster = Math.ceil(Date.now() / 1000)
    let json

    try {
      const res = await fetch(
        `${window.location.origin}/version.json?${cacheBuster}`
      )

      // console.log('refreshVersion', { res })
      if (res.ok) {
        json = await res.json()
      } else {
        throw new Error('request failed')
      }
    } catch (e) {
      json = false
    }

    if (isPlainObject(json) && json.build > BUILD_VERSION) {
      setHasNewVersion(true)
    }
  }, [setHasNewVersion])

  // start polling for updates
  React.useEffect(() => {
    refreshVersion()
    const interval = setInterval(() => refreshVersion(), POLLING_INTERVAL)
    return () => {
      clearInterval(interval)
    }
  }, [refreshVersion])

  // some locations in the code will cause an app refresh on their own, so they only need a way to update the refresh state
  const markAsRefreshed = React.useCallback(() => {
    setHasNewVersion(false)
  }, [setHasNewVersion])

  // other parts of the code need to update the state and cause a reload
  const doRefreshCheck = React.useCallback(() => {
    if (hasNewVersion) {
      setHasNewVersion(false)
      // if we reload too quickly the above call doesn't have time to finish and update local storage
      setTimeout(() => {
        window.location.reload()
      }, 500)
    }
  }, [setHasNewVersion, hasNewVersion])

  return (
    <AppRefreshContext.Provider
      value={{
        hasNewVersion,
        doRefreshCheck,
        markAsRefreshed
      }}
    >
      {children}
    </AppRefreshContext.Provider>
  )
}
